/**
 * Naam Darbar — SkeletonImg
 *
 * An <img> with a shimmer skeleton shown while it loads. The wrapper owns
 * the layout (size, rounding, borders); the image fills it and fades in.
 *
 * - Cached images that are already complete skip the shimmer entirely.
 * - Pass `fallback` to render alternate content (e.g. an initials avatar)
 *   when the image fails to load.
 *
 * Usage:
 *   <SkeletonImg src={url} alt={name} className="h-40 w-full" />
 *   <SkeletonImg {...cdnAvatar(img, 128)} alt={name}
 *     className="h-16 w-16 rounded-full" imgClassName="object-cover object-top"
 *     fallback={<InitialsAvatar />} />
 */

import { useEffect, useRef, useState, type ImgHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

interface SkeletonImgProps extends ImgHTMLAttributes<HTMLImageElement> {
  /** Wrapper classes — sizing, rounding, borders. The img fills the wrapper. */
  className?: string;
  /** Classes for the inner <img>; defaults to `object-cover`. */
  imgClassName?: string;
  /** Rendered instead of the image when it fails to load. */
  fallback?: ReactNode;
  /**
   * When false, the image keeps its intrinsic size (e.g. object-contain
   * posters) instead of filling the wrapper; the wrapper reserves a
   * min-height while loading so the shimmer has visible area.
   */
  fill?: boolean;
}

export default function SkeletonImg({
  className,
  imgClassName,
  fallback,
  fill = true,
  alt = '',
  onLoad,
  onError,
  ...imgProps
}: SkeletonImgProps) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Cached images may be complete before onLoad can fire — skip the shimmer.
  useEffect(() => {
    const el = imgRef.current;
    if (el?.complete && el.naturalWidth > 0) setLoaded(true);
  }, []);

  if (errored && fallback) return <>{fallback}</>;

  return (
    <span
      className={cn(
        'relative block overflow-hidden',
        !fill && !loaded && !errored && 'min-h-40',
        className,
      )}
    >
      {!loaded && !errored && (
        <span aria-hidden="true" className="skeleton-shimmer absolute inset-0" />
      )}
      <img
        ref={imgRef}
        {...imgProps}
        alt={alt}
        onLoad={(e) => {
          setLoaded(true);
          onLoad?.(e);
        }}
        onError={(e) => {
          setErrored(true);
          onError?.(e);
        }}
        className={cn(
          'transition-opacity duration-300',
          fill && 'h-full w-full',
          imgClassName ?? (fill ? 'object-cover' : undefined),
          loaded ? 'opacity-100' : 'opacity-0',
        )}
      />
    </span>
  );
}
